/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */
import { Adjust } from "@awesome-cordova-plugins/adjust";
import * as React from "react";
import { useLocation, Redirect, useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";

import { UserDeviceToken, RegisteredStatusEnum } from "@/api/generated/api";
import { AppLoadError, LoadFailure } from "@/components/AppLoadError";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { PasswordResetPage } from "@/pages/password_reset/PasswordResetPage";
import { PasswordResetSendLinkPage } from "@/pages/password_reset_send_link/PasswordResetSendLinkPage";
import { isHandledByApiInterceptor } from "@/utilities/apiErrorReporting";
import { isNativeApp, sanitizeDeviceToken, resolveDeviceTypeForNativeApp } from "@/utilities/capacitorLogic";
import { DiagnosisPlannerLocationState, isDiagnosisPlannerFlow } from "@/utilities/diagnosisPlannerFlow";
import { clearExpoRegistration, isExpoRegistration } from "@/utilities/expoRegistration";
import { consumeIntent } from "@/utilities/intentNavigator";
import { getAppDeviceToken } from "@/utilities/storage";
import { SessionState } from "@/view_models/atoms";

const ignoreAuths = [
  { path: "/password_reset/new", Component: PasswordResetSendLinkPage },
  { path: "/password_reset", Component: PasswordResetPage },
];

export const WithAuth: React.FC<React.PropsWithChildren<any>> = (props) => {
  const api = PropallyApiFactory();
  const [session, setSession] = useRecoilState(SessionState);
  const location = useLocation<DiagnosisPlannerLocationState>();
  const history = useHistory(); // useHistoryフックを追加
  const path = location.pathname;

  const [loadFailure, setLoadFailure] = React.useState<LoadFailure | null>(null);
  const [isCheckingSession, setIsCheckingSession] = React.useState(true);

  const ignoreAuth = ignoreAuths.find((h) => h.path === path);

  React.useEffect(() => {
    (async () => {
      try {
        // Adjust Attribution から network を取得（取得できた場合のみ返却）
        // 検証環境ではAdjustを初期化しないため、ネイティブアプリにて適切に値が返らない。
        // またフェイルセーフとしてタイムアウトを設けてAPI呼び出しをブロックしないようにする
        const getAdjustNetwork = async (): Promise<string | undefined> => {
          if (!isNativeApp) return undefined;
          const fetch = async () => {
            try {
              const hasGetter = typeof (Adjust as any)?.getAttribution === "function";
              if (!hasGetter) return undefined;
              const attribution = await Adjust.getAttribution();
              const v = attribution?.network?.trim();
              return v || undefined;
            } catch (_) {
              return undefined;
            }
          };
          const timeoutMs = 800;
          const timeout = new Promise<string | undefined>((resolve) => setTimeout(() => resolve(undefined), timeoutMs));
          return Promise.race([fetch(), timeout]);
        };
        const adjust_network = await getAdjustNetwork();
        await PropallyApiFactory().v1SessionGet(adjust_network, { withCredentials: true });

        // 1. セッション更新
        setSession({ logged_in: true });

        // 2. アプリからのアクセス時はデバイス登録
        if (isNativeApp) {
          const deviceToken = sanitizeDeviceToken(getAppDeviceToken());
          const deviceType = resolveDeviceTypeForNativeApp();

          if (deviceToken && deviceType) {
            const userDeviceToken: UserDeviceToken = {
              device_token: deviceToken,
              device_type: deviceType,
            };

            try {
              // 冪等なので重複OK
              await api.v1UserDeviceTokenPost(userDeviceToken, { withCredentials: true });
            } catch (e) {
              // API エラーは PropallyApiFactory の interceptor が Sentry へ送るため info に留める。
              // ここで warn にすると CaptureConsole が同じ失敗を別イシューとして重複計上する
              // （PROPALLY_WEB-28E）。登録失敗は次回起動時に再登録されるため画面上の影響もない。
              if (isHandledByApiInterceptor(e)) console.info("device token register failed:", e);
              else console.warn("device token register failed:", e);
            }
          } else {
            console.info("デバイストークン登録をスキップ: tokenまたはdeviceTypeが無効です");
          }
        }
        const user = await api.v1UserGet({ withCredentials: true });
        const isRegistration = user.data.data_first_registered_status === RegisteredStatusEnum.Registration;
        const isDiagnosisPending = !isRegistration && user.data.is_diagnosis_complete === false;
        const shouldShowDiagnosis = isDiagnosisPending && location.pathname !== "/diagnosis";
        // エキスポ導線の目印（Trello #241）は、スタイル診断が未完了のユーザーをこの端末でだけ強制遷移から外すためのもの。
        // 完了済み・既存ユーザーには不要なので、判明した時点で消して次のアカウントへ持ち越さない
        if (user.data.is_investment_style_diagnosis_complete !== false) clearExpoRegistration();
        // 投資スタイル診断は投資力診断の後段。回答をメモリにしか持たない都合で途中離脱時は最初からやり直しになるため、
        // 未完了のまま起動したらイントロへ戻す。既存ユーザーはフラグ自体が返らないので対象外となる。
        // エキスポ導線はスタイル診断を飛ばすため、目印がある間は戻さない（ホームのミニ表示から任意で受けられる）
        const shouldShowStyleDiagnosis =
          !isRegistration &&
          !isDiagnosisPending &&
          user.data.is_investment_style_diagnosis_complete === false &&
          !location.pathname.startsWith("/investment_style_diagnosis") &&
          !isExpoRegistration();
        if (isRegistration) {
          // 3-1. 初回登録未完了の場合は first_account に遷移
          if (location.pathname !== "/first_account") history.replace("/first_account");
        } else if (shouldShowDiagnosis) {
          history.replace("/diagnosis");
        } else if (shouldShowStyleDiagnosis) {
          // 登録導線としての強制遷移。state を付けないため、診断フロー側では
          // ステップ表示のある登録導線として扱われる（useFromHomeEntry が false になる）
          history.replace("/investment_style_diagnosis/intro");
        } else {
          // 3-2. 通知の行き先があれば最優先で復元
          const pending = consumeIntent();
          if (pending) history.replace(pending);
          // 診断完了後の選択は起動中だけ。未マッチでも再起動時にはホームへ進む。
          else if (isDiagnosisPlannerFlow(location)) history.replace("/mypage");
        }
      } catch (e) {
        if (e.response?.status === 401) setSession({ logged_in: false });
        else setLoadFailure(e.response?.status === 503 ? "maintenance" : "network");
      } finally {
        setIsCheckingSession(false); // 認証チェック完了
      }
    })();
  }, []);

  // 4. 認証チェック中は何も描画しない（ちらつき防止）
  if (isCheckingSession) return null;

  // 5. 認証スキップ対象パスならそのまま描画
  if (ignoreAuth) return <ignoreAuth.Component />;
  if (loadFailure) return <AppLoadError kind={loadFailure} />;

  // 6. ログイン済みなら中身を表示、そうでなければログイン画面へ
  return session.logged_in ? props.children : <Redirect to="/login" />;
};
